import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  CalendarIcon,
  Search,
  Filter,
  X,
  FileText,
  Users,
  Clock,
  CheckCircle,
  Star,
  CheckCircle2,
  PanelRightClose,
  PanelRight,
} from "lucide-react";
import { CalendarWeekHeader } from "./CalendarWeekHeader";
import { CalendarHoursColumn } from "./CalendarHoursColumn";
import { CalendarDayColumn } from "./CalendarDayColumn";
import { EventSheet } from "./EventSheet";
import { CalendarEvent, CalendarTask } from "@/types/calendar.types";
import { INITIAL_SCROLL_OFFSET, formatTime } from "./calendar-utils";
import { useCalendarData } from "@/hooks/calendar/useCalendarData";
import {
  getWeekDays,
  formatDateString,
} from "@/views/calendar";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Calendar } from "@/components/ui/Calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/Popover";
import { Avatar } from "@/components/ui/Avatar";
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/Functions";
import { IconCalendarClock, IconProgress } from "@/components/icons";

type EventType = CalendarEvent["type"];

const eventTypeConfig: Record<
  EventType,
  { label: string; icon: React.ElementType; color: string }
> = {
  contract: { label: "Contracts", icon: FileText, color: "bg-violet-500" },
  meeting: { label: "Meetings", icon: Users, color: "bg-cyan-500" },
  deadline: { label: "Deadlines", icon: Clock, color: "bg-red-500" },
  review: { label: "Reviews", icon: CheckCircle, color: "bg-amber-500" },
};

const priorityColors: Record<CalendarTask["priority"], string> = {
  high: "text-task-progress-high",
  medium: "text-task-progress-medium",
  low: "text-task-progress-low",
};

function CalendarPage() {
  const { events: calendarEvents, tasks: calendarTasks, contacts: calendarContacts, getContactById, getUpcomingEvents } = useCalendarData();
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    return startOfWeek;
  });

  const [weekDays, setWeekDays] = useState<Date[]>(() =>
    getWeekDays(currentWeekStart)
  );
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<Set<EventType>>(
    new Set(["contract", "meeting", "deadline", "review"])
  );
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tasks, setTasks] = useState<CalendarTask[]>(calendarTasks);

  // Sync when switching between demo and prod routes
  useEffect(() => {
    setTasks(calendarTasks);
  }, [calendarTasks]);
  const [viewMode, setViewMode] = useState<"calendar" | "tasks">("calendar");

  const hoursScrollRef = useRef<HTMLDivElement>(null);
  const daysScrollRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasScrolledRef = useRef(false);

  const today = new Date();
  const todayStr = formatDateString(today);

  // Update week days when week start changes
  useEffect(() => {
    setWeekDays(getWeekDays(currentWeekStart));
  }, [currentWeekStart]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter events based on search and type filters
  const filteredEvents = useMemo(() => {
    return calendarEvents.filter((event) => {
      if (!activeFilters.has(event.type)) return false;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = event.title.toLowerCase().includes(query);
        const matchesDescription = event.description?.toLowerCase().includes(query);
        const matchesParticipants = event.participants.some((p) => {
          const contact = getContactById(p);
          return contact?.name.toLowerCase().includes(query) || p.toLowerCase().includes(query);
        });
        if (!matchesTitle && !matchesDescription && !matchesParticipants) {
          return false;
        }
      }
      return true;
    });
  }, [searchQuery, activeFilters]);

  // Group filtered events by day
  const eventsByDay: Record<string, CalendarEvent[]> = useMemo(() => {
    const result: Record<string, CalendarEvent[]> = {};
    weekDays.forEach((day) => {
      const dayStr = formatDateString(day);
      result[dayStr] = filteredEvents.filter((e) => e.date === dayStr);
    });
    return result;
  }, [weekDays, filteredEvents]);

  // Get upcoming events
  const upcomingEvents = useMemo(() => getUpcomingEvents(8), []);

  // Tasks stats
  const taskStats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.completed).length;
    const todayTasks = tasks.filter((t) => t.dueDate === todayStr && !t.completed).length;
    const overdue = tasks.filter((t) => t.dueDate && t.dueDate < todayStr && !t.completed).length;
    return { total, completed, todayTasks, overdue };
  }, [tasks, todayStr]);

  // Check if today is in the current week
  const isTodayInWeek = weekDays.some(
    (day) => formatDateString(day) === todayStr
  );

  // Initial scroll to 9 AM
  useEffect(() => {
    const scrollToInitial = () => {
      if (!hasScrolledRef.current && hoursScrollRef.current) {
        hoursScrollRef.current.scrollTop = INITIAL_SCROLL_OFFSET;
        daysScrollRefs.current.forEach((ref) => {
          if (ref) ref.scrollTop = INITIAL_SCROLL_OFFSET;
        });
        hasScrolledRef.current = true;
      }
    };
    scrollToInitial();
    const timeoutId = setTimeout(scrollToInitial, 100);
    return () => clearTimeout(timeoutId);
  }, [weekDays]);

  // Synchronized scrolling
  const handleHoursScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    daysScrollRefs.current.forEach((ref) => {
      if (ref) ref.scrollTop = scrollTop;
    });
  }, []);

  const handleDayScroll = useCallback(
    (index: number) => (e: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = e.currentTarget.scrollTop;
      if (hoursScrollRef.current) {
        hoursScrollRef.current.scrollTop = scrollTop;
      }
      daysScrollRefs.current.forEach((ref, idx) => {
        if (ref && idx !== index) ref.scrollTop = scrollTop;
      });
    },
    []
  );

  // Navigation
  const goToNextWeek = useCallback(() => {
    setCurrentWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + 7);
      return next;
    });
    hasScrolledRef.current = false;
  }, []);

  const goToPreviousWeek = useCallback(() => {
    setCurrentWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() - 7);
      return next;
    });
    hasScrolledRef.current = false;
  }, []);

  const goToToday = useCallback(() => {
    const todayDate = new Date();
    const dayOfWeek = todayDate.getDay();
    const startOfWeek = new Date(todayDate);
    startOfWeek.setDate(todayDate.getDate() - dayOfWeek);
    setCurrentWeekStart(startOfWeek);
    hasScrolledRef.current = false;
  }, []);

  // Date picker handler
  const handleDateSelect = useCallback((date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const dayOfWeek = date.getDay();
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - dayOfWeek);
      setCurrentWeekStart(startOfWeek);
      hasScrolledRef.current = false;
      setDatePickerOpen(false);
    }
  }, []);

  // Event click handler
  const handleEventClick = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
    setSheetOpen(true);
  }, []);

  // Toggle task completion
  const toggleTaskComplete = useCallback((taskId: string) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t))
    );
  }, []);

  // Toggle filter
  const toggleFilter = useCallback((type: EventType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const selectAllFilters = useCallback(() => {
    setActiveFilters(new Set(["contract", "meeting", "deadline", "review"]));
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const activeFilterCount = activeFilters.size;
  const allFiltersActive = activeFilterCount === 4;

  const weekLabel = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (!start || !end) return "";
    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });
    const year = end.getFullYear();
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${year}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${year}`;
  }, [weekDays]);

  return (
    <div className="flex h-full">

      <div className={cn("grid w-full grid-cols-1", sidebarOpen && "grid-cols-[1fr_auto]")}>
      {/* Main Calendar Area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-y-scroll">
        {/* Header */}
        <header className="py-3 px-4 sticky top-0 bg-background z-40 border-b border-border/50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CalendarIcon className="size-5 text-muted-foreground" />
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "calendar" | "tasks")}>
                <TabsList variant="pill" indicatorColor="secondary" className="text-xs">
                  <TabsTab value="calendar">Calendar</TabsTab>
                  <TabsTab value="tasks">
                    Tasks
                    {taskStats.todayTasks + taskStats.overdue > 0 && (
                      <span className="text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-4 text-center font-semibold">
                        {taskStats.todayTasks + taskStats.overdue}
                      </span>
                    )}
                  </TabsTab>
                </TabsList>
              </Tabs>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Input
                  startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search calendar"
                />
                {searchQuery && (
                  <button
                    aria-label="Clear search"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>

              {/* Filter Popover */}
              <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("h-8 gap-1.5", !allFiltersActive && "border-primary text-primary")}
                    >
                      <Filter className="size-4" />
                      <span className="hidden sm:inline">Filter</span>
                      {!allFiltersActive && (
                        <span className="size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  }
                />
                <PopoverContent align="end" className="w-[220px] p-2 bg-popover border border-border rounded-lg shadow-lg">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between px-2 py-1.5 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">Event Types</span>
                      <button onClick={selectAllFilters} className="text-xs text-primary hover:underline">
                        Select all
                      </button>
                    </div>
                    {(Object.entries(eventTypeConfig) as [EventType, typeof eventTypeConfig[EventType]][]).map(
                      ([type, config]) => {
                        const Icon = config.icon as React.ComponentType<{ className?: string }>;
                        const isActive = activeFilters.has(type);
                        return (
                          <button
                            key={type}
                            onClick={() => toggleFilter(type)}
                            className={cn(
                              "flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors",
                              isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-muted"
                            )}
                          >
                            <div className={cn("size-2 rounded-full", config.color)} />
                            <Icon className="size-4" />
                            <span className="flex-1 text-left">{config.label}</span>
                            {isActive && <CheckCircle className="size-4 text-primary" />}
                          </button>
                        );
                      }
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Date Picker */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger
                  render={
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <CalendarIcon className="size-4" />
                      <span className="hidden sm:inline">{weekLabel}</span>
                    </Button>
                  }
                />
                <PopoverContent align="end" className="w-auto p-0 bg-popover border border-border rounded-lg shadow-lg">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} defaultMonth={currentWeekStart} />
                  <div className="border-t border-border p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        goToToday();
                        setSelectedDate(new Date());
                        setDatePickerOpen(false);
                      }}
                    >
                      Go to Today
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {!isTodayInWeek && (
                <Button variant="default" size="sm" className="h-8" onClick={goToToday}>
                  Today
                </Button>
              )}

              {/* Sidebar Toggle */}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="h-8 w-8"
              >
                {sidebarOpen ? <PanelRightClose className="size-4" /> : <PanelRight className="size-4" />}
              </Button>
            </div>
          </div>

          {searchQuery && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""} matching "{searchQuery}"
              </span>
              <button onClick={clearSearch} className="text-primary hover:underline text-xs">
                Clear
              </button>
            </div>
          )}
        </header>

        <EventSheet event={selectedEvent} open={sheetOpen} onOpenChange={setSheetOpen} />

        {viewMode === "calendar" ? (
          /* Calendar Grid */
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col h-full overflow-x-auto">
              <CalendarWeekHeader weekDays={weekDays} onPreviousWeek={goToPreviousWeek} onNextWeek={goToNextWeek} />
              <div className="flex flex-1 min-w-full w-max overflow-hidden">
                <CalendarHoursColumn onScroll={handleHoursScroll} scrollRef={hoursScrollRef} />
                {weekDays.map((day, dayIndex) => {
                  const dayStr = formatDateString(day);
                  const dayEvents = eventsByDay[dayStr] || [];
                  return (
                    <CalendarDayColumn
                      key={day.toISOString()}
                      day={day}
                      dayIndex={dayIndex}
                      events={dayEvents}
                      today={today}
                      isTodayInWeek={isTodayInWeek}
                      currentTime={currentTime}
                      onScroll={handleDayScroll}
                      scrollRef={(el) => {
                        daysScrollRefs.current[dayIndex] = el;
                      }}
                      onEventClick={handleEventClick}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Tasks View */
          <div className="flex-1 overflow-y-auto p-6">
            {/* Stats row */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="rounded-xl border-2 border-border p-4">
                <p className="text-xs text-muted-foreground">Open</p>
                <p className="text-2xl font-semibold text-foreground tabular-nums">{taskStats.total - taskStats.completed}</p>
              </div>
              <div className="rounded-xl border-2 border-border p-4">
                <p className="text-xs text-muted-foreground">Due Today</p>
                <p className="text-2xl font-semibold text-foreground tabular-nums">{taskStats.todayTasks}</p>
              </div>
              <div className="rounded-xl border-2 border-border p-4">
                <p className={cn("text-2xl font-semibold tabular-nums", taskStats.overdue > 0 ? "text-red-500" : "text-foreground")}>{taskStats.overdue}</p>
              </div>
              <div className="rounded-xl border-2 border-border p-4">
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-2xl font-semibold text-foreground tabular-nums">{taskStats.completed}</p>
              </div>
            </div>

            {/* Task list */}
            <div className="rounded-2xl border-2 border-border overflow-hidden">
              <div className="bg-muted/50 px-4 py-3 border-b border-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">All Tasks</h3>
                <span className="text-xs text-muted-foreground">{tasks.length} total</span>
              </div>
              <div>
                {tasks
                  .sort((a, b) => {
                    if (a.completed !== b.completed) return a.completed ? 1 : -1;
                    const priorityOrder = { high: 0, medium: 1, low: 2 };
                    return priorityOrder[a.priority] - priorityOrder[b.priority];
                  })
                  .map((task) => {
                    const contact = task.contactId ? getContactById(task.contactId) : undefined;
                    const isDueToday = task.dueDate === todayStr;
                    const isOverdue = !task.completed && task.dueDate && task.dueDate < todayStr;
                    const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
                    const isDueTomorrow = task.dueDate === tomorrow;

                    const formatDue = () => {
                      if (!task.dueDate) return "";
                      if (isOverdue) return "Overdue";
                      if (isDueToday) return task.dueTime ? `Today ${task.dueTime}` : "Today";
                      if (isDueTomorrow) return task.dueTime ? `Tomorrow ${task.dueTime}` : "Tomorrow";
                      return new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    };

                    return (
                      <div
                        key={task.id}
                        className={cn(
                          "flex items-start gap-3 px-4 py-3 border-b border-border/30 last:border-b-0 hover:bg-muted/30 transition-colors",
                          task.completed && "opacity-50"
                        )}
                      >
                        <button
                          onClick={() => toggleTaskComplete(task.id)}
                          className="shrink-0 mt-0.5"
                        >
                          {task.completed ? (
                            <CheckCircle2 className="size-5 text-primary" />
                          ) : (
                            <IconProgress className={cn("size-5", priorityColors[task.priority])} />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={cn("text-sm font-medium text-foreground", task.completed && "line-through")}>{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{task.description}</p>
                          )}
                          {task.dueDate && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <IconCalendarClock className="size-4" />
                              <span>{formatDue()}</span>
                            </div>
                          )}
                        </div>
                        {contact && (
                          <Avatar src={contact.avatar} fallback={contact.name.charAt(0)} className="size-6 shrink-0" aria-label={contact.name} />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      {sidebarOpen && (
        <aside className="w-[320px] border-l border-border bg-card/50 flex flex-col shrink-0 overflow-y-auto">
          <Tabs defaultValue="upcoming" className="flex flex-col h-full">
            {/* Sidebar Tabs */}
            <TabsList variant="underline" className="w-full px-2 pt-1 border-b border-border h-14 sticky top-0 bg-background">
              <TabsTab value="upcoming" className="flex-1">
                Upcoming
              </TabsTab>
              <TabsTab value="tasks" className="flex-1 gap-1">
                Tasks
                {taskStats.todayTasks > 0 && (
                  <Badge variant="default" size="sm" className="ml-1">
                    {taskStats.todayTasks}
                  </Badge>
                )}
              </TabsTab>
              <TabsTab value="contacts" className="flex-1">
                Contacts
              </TabsTab>
            </TabsList>

            {/* Upcoming Meetings */}
            <TabsPanel value="upcoming" className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Upcoming</h3>
                  <span className="text-xs text-muted-foreground">{upcomingEvents.length} events</span>
                </div>
                {upcomingEvents.map((event) => {
                  const contact = event.contactId ? getContactById(event.contactId) : undefined;
                  const typeConfig = eventTypeConfig[event.type];
                  const isToday = event.date === todayStr;
                  return (
                    <button
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className="cursor-pointer w-full p-3 bg-accent/50 rounded-xl text-left hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={cn("size-2 rounded-full mt-1.5 shrink-0", typeConfig.color)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span className={cn(isToday && "text-primary font-medium")}>
                              {isToday ? "Today" : new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                            </span>
                            <span>·</span>
                            <span>{formatTime(event.startTime)}</span>
                          </div>
                          {contact && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <Avatar src={contact.avatar} fallback={contact.name.charAt(0)} className="size-5" />
                              <span className="text-xs text-muted-foreground truncate">{contact.name}</span>
                            </div>
                          )}
                        </div>
                        {/* {event.meetingLink && <Video className="size-4 text-cyan-500 shrink-0" />} */}
                      </div>
                    </button>
                  );
                })}
              </div>
            </TabsPanel>

            {/* Tasks / To-Do List */}
            <TabsPanel value="tasks" className="flex-1 overflow-y-auto p-3">
              <div className="space-y-3">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-background border border-border rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-foreground">{taskStats.total - taskStats.completed}</p>
                    <p className="text-[10px] text-muted-foreground">Open</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-foreground">{taskStats.todayTasks}</p>
                    <p className="text-[10px] text-muted-foreground">Today</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-2 text-center">
                    <p className={cn("text-lg font-semibold", taskStats.overdue > 0 ? "text-red-500" : "text-foreground")}>
                      {taskStats.overdue}
                    </p>
                    <p className="text-[10px] text-muted-foreground">Overdue</p>
                  </div>
                </div>

                {/* Task List */}
                <div className="space-y-1">
                  {tasks
                    .filter((t) => !t.completed)
                    .sort((a, b) => {
                      const priorityOrder = { high: 0, medium: 1, low: 2 };
                      return priorityOrder[a.priority] - priorityOrder[b.priority];
                    })
                    .map((task) => {
                      const contact = task.contactId ? getContactById(task.contactId) : undefined;
                      const isOverdue = task.dueDate && task.dueDate < todayStr;
                      const isDueToday = task.dueDate === todayStr;
                      const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
                      const isDueTomorrow = task.dueDate === tomorrow;

                      const formatDue = () => {
                        if (!task.dueDate) return "";
                        if (isOverdue) return "Overdue";
                        if (isDueToday) return task.dueTime ? `Today ${task.dueTime}` : "Today";
                        if (isDueTomorrow) return task.dueTime ? `Tomorrow ${task.dueTime}` : "Tomorrow";
                        return new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      };

                      return (
                        <div
                          key={task.id}
                          className="flex items-start gap-2.5 p-2.5 bg-neutral-100 dark:bg-transparent rounded-lg hover:dark:bg-muted/50 transition-colors"
                        >
                          <button
                            onClick={() => toggleTaskComplete(task.id)}
                            className="mt-0.5 shrink-0"
                          >
                            <IconProgress className={cn("size-5", priorityColors[task.priority])} />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{task.title}</p>
                            {task.dueDate && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                <IconCalendarClock className="size-4" />
                                <span>{formatDue()}</span>
                              </div>
                            )}
                          </div>
                          {contact && (
                            <Avatar src={contact.avatar} fallback={contact.name.charAt(0)} className="size-6 shrink-0" aria-label={contact.name} />
                          )}
                        </div>
                      );
                    })}
                </div>

                {/* Completed Tasks */}
                {tasks.filter((t) => t.completed).length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Completed ({tasks.filter((t) => t.completed).length})</p>
                    <div className="space-y-1">
                      {tasks
                        .filter((t) => t.completed)
                        .slice(0, 3)
                        .map((task) => (
                          <div
                            key={task.id}
                            className="flex items-center gap-2.5 p-2 bg-muted/30 rounded-lg opacity-60"
                          >
                            <button onClick={() => toggleTaskComplete(task.id)} className="text-green-500 shrink-0">
                              <CheckCircle2 className="size-4" />
                            </button>
                            <p className="text-sm text-muted-foreground line-through flex-1 truncate">{task.title}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsPanel>

            {/* Contacts */}
            <TabsPanel value="contacts" className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Deal Contacts</h3>
                  <span className="text-xs text-muted-foreground">{calendarContacts.length}</span>
                </div>
                {calendarContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="p-3 bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar src={contact.avatar} fallback={contact.name.charAt(0)} className="size-9 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-foreground truncate">{contact.name}</p>
                          {contact.isStarred && <Star className="size-3 fill-amber-400 text-amber-400 shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{contact.role}</p>
                        <p className="text-[10px] text-muted-foreground/70 truncate">{contact.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsPanel>
          </Tabs>
        </aside>
      )}

      </div>
    </div>
  );
}

export default CalendarPage;
